import { Component, effect, inject, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { CommonModule } from '@angular/common';
import { FundingSourceService } from './funding-sources.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { FundingSource, FundingSourceDto } from '@/types';
import { DataTableRowActions, DataTableServerQuery, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { getFundingSourceDataTableStructure } from './utils/funding-source.datatable-object';
import { FundingSourceRepository } from '@/store/funding-source-state/funding-source-state.repository';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DialogObject } from '@/components/dialog/dialog.types';
import { getFundingSourceCreateFormObject } from './utils/funding-source-create.form-object';
import { getFundingSourceAddDrawerObject } from './utils/funding-source.add-drawer-object';
import { fundingSourceSchema } from '@/validations/funding-source.validation';
import { getFundingSourceUpdateDrawerObject } from './utils/funding-source.update-drawer-object';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { getFundingSourceUpdateFormObject } from './utils/funding-source-update.form-object';
import { getFundingSourceDeleteObject } from './utils/funding-source.delete-dialog-object';

@Component({
  selector: 'sm-funding-sources',
  imports: [CommonModule, PageHeaderComponent, DataTableBuilderComponent, DrawerComponent, DialogComponent],
  templateUrl: './funding-sources.component.html',
  styleUrl: './funding-sources.component.scss',
})
export class FundingSourcesComponent {
  readonly fundingSourceService = inject(FundingSourceService);
  readonly fundingSourceRepository = inject(FundingSourceRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  breadcrumb = [{ label: 'Paramètrage', routerLink: ['settings'] }, { label: 'fundingSources.title' }];

  @ViewChild('drawer') drawer!: DrawerComponent;
  fundingSourceDrawer?: DrawerObject;

  @ViewChild('dialog') dialog!: DialogComponent;
  fundingSourceDialog?: DialogObject;

  formObject = getFormBuilderObjectFactory();

  data: FundingSource[] = [];
  totalRecords = 0;
  serverQuery: DataTableServerQuery = createServerQuery({ initialPageSize: 10, initialSortBy: 'updatedAt', initialSortOrder: 'desc' });
  rowActions: DataTableRowActions = {
    editAction: () => {
      this.formObject = getFundingSourceUpdateFormObject({ store: this.fundingSourceRepository });
      this.fundingSourceDrawer = getFundingSourceUpdateDrawerObject({
        store: this.fundingSourceRepository,
        formObject: this.formObject,
        action: () => this.update(),
        toggleVisibility: () => this.drawer.toggleVisible(),
      });
      this.drawer.toggleVisible();
    },
    deleteAction: (row: FundingSource) => {
      this.fundingSourceDialog = getFundingSourceDeleteObject({
        store: this.fundingSourceRepository,
        action: () => {
          this.delete(row.id);
          this.dialog.toggleVisible();
        },
        toggleVisiblity: () => this.dialog.toggleVisible(),
      });
      this.dialog.toggleVisible();
    },
  };
  dataTableObject?: DynamicDataTable = getFundingSourceDataTableStructure({
    createAction: () => {
      this.formObject = getFundingSourceCreateFormObject({ store: this.fundingSourceRepository });
      this.fundingSourceDrawer = getFundingSourceAddDrawerObject({
        store: this.fundingSourceRepository,
        formObject: this.formObject,
        action: () => this.add(),
        toggleVisibility: () => this.drawer.toggleVisible(),
      });
      this.drawer.toggleVisible();
    },
    store: this.fundingSourceRepository,
    serverQuery: this.serverQuery,
    rowActions: this.rowActions,
  });
  loading = true;

  constructor() {
    let firstRun = true;

    effect(() => {
      const page = this.serverQuery.page();
      const size = this.serverQuery.pageSize();
      const sortBy = this.serverQuery.sortBy();
      const sortOrder = this.serverQuery.sortOrder();
      const search = this.serverQuery.search();
      const sort = sortBy ? `${sortBy},${sortOrder}` : '';

      if (firstRun) {
        firstRun = false;
        return;
      }

      this.loadData(page, size, search, sort);
    });
  }

  loadData(page = 0, size = 10, search = '', sort = '') {
    this.loading = true;
    this.fundingSourceService.getFundingSources({ page, size, search, sort }).subscribe({
      next: data => {
        this.data = data.content;
        this.totalRecords = data.totalElements;
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message,
        });
      },
    });
  }

  add() {
    const dto = this.fundingSourceRepository.get<FundingSourceDto>('createDto');
    const result = fundingSourceSchema.safeParse(dto);
    if (result.success) {
      this.fundingSourceService.add(dto).subscribe({
        next: () => {
          this.drawer.toggleVisible();
          this.loadData();
          this.fundingSourceRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('fundingSources.add.success'),
          });
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || this.translate.instant('fundingSources.add.error'),
          });
        },
      });
    } else {
      this.fundingSourceRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  update() {
    const id = this.fundingSourceRepository.getNested<number>('response', 'id');
    const dto = this.fundingSourceRepository.get<FundingSourceDto>('updateDto');
    const result = fundingSourceSchema.safeParse(dto);
    if (result.success) {
      this.fundingSourceService.update(id, dto).subscribe({
        next: () => {
          this.drawer.toggleVisible();
          this.loadData();
          this.fundingSourceRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('fundingSources.update.success'),
          });
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || this.translate.instant('fundingSources.update.error'),
          });
        },
      });
    } else {
      this.fundingSourceRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  delete(id: number) {
    this.fundingSourceService.delete(id).subscribe({
      next: () => {
        this.loadData();
        this.fundingSourceRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('fundingSources.delete.success'),
        });
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('fundingSources.delete.error'),
        });
      },
    });
  }
}
